function parsePairItWithRecommendationHtml(text) {
  const parsed = new DOMParser().parseFromString(text, 'text/html');
  const payload =
    parsed.querySelector('cart-pair-it-with-payload') ||
    parsed.querySelector('.cart-pair-it-with__inner')?.closest('cart-pair-it-with-payload');

  if (payload) {
    const inner = payload.querySelector('.cart-pair-it-with__inner');
    return inner ? inner.outerHTML : payload.innerHTML.trim();
  }

  const inner = parsed.querySelector('.cart-pair-it-with__inner');
  return inner ? inner.outerHTML : '';
}

function refreshCartPairItWithElements(scope) {
  const root = scope || getCartDrawerCartRoot?.() || document;
  root.querySelectorAll('cart-pair-it-with').forEach((el) => {
    if (typeof customElements.upgrade === 'function') {
      customElements.upgrade(el);
    }
    if (typeof el.refresh === 'function') {
      el.refresh();
    }
  });
}

if (!customElements.get('cart-pair-it-with')) {
  class CartPairItWith extends HTMLElement {
    static get observedAttributes() {
      return ['data-product-id', 'data-url', 'data-anchor-key'];
    }

    connectedCallback() {
      this.bindEvents();
      this.scheduleLoad();
    }

    disconnectedCallback() {
      this._abortController?.abort();
      this._unsubscribe?.();
      this._unsubscribe = null;
      this._loadScheduled = false;
    }

    attributeChangedCallback(name, oldValue, newValue) {
      if (!this.isConnected || oldValue === newValue) return;
      if (name !== 'data-product-id' && name !== 'data-url' && name !== 'data-anchor-key') {
        return;
      }
      this.hidden = true;
      this.innerHTML = '';
      this.scheduleLoad();
    }

    refresh() {
      if (this.querySelector('.cart-pair-it-with__inner')) {
        this.hidden = false;
        this.repositionAfterAnchor(this.dataset.anchorKey);
        this.closest('tr.cart-pair-it-with-row')?.removeAttribute('aria-hidden');
        return;
      }
      this.scheduleLoad();
    }

    scheduleLoad() {
      if (this._loadScheduled) return;
      this._loadScheduled = true;
      window.requestAnimationFrame(() => {
        this._loadScheduled = false;
        this.loadRecommendations();
      });
    }

    getCartDrawer() {
      return typeof getActiveCartDrawerElement === 'function' ? getActiveCartDrawerElement() : null;
    }

    isDismissed() {
      const cartDrawer = this.getCartDrawer();
      const productId = this.dataset.productId;
      if (!cartDrawer || !productId || typeof cartDrawer.isPairItWithDismissed !== 'function') {
        return false;
      }
      return cartDrawer.isPairItWithDismissed(productId);
    }

    getActiveAnchor() {
      const productId = this.dataset.productId;
      const anchorKey = this.dataset.anchorKey;
      const url = this.dataset.url || this.buildRecommendationUrl(productId);
      if (!productId || !anchorKey || !url) return null;
      return { productId, anchorKey, url };
    }

    buildRecommendationUrl(productId) {
      const template = this.dataset.url;
      if (!template) return '';
      try {
        const parsed = new URL(template, window.location.origin);
        parsed.searchParams.set('product_id', String(productId));
        return `${parsed.pathname}${parsed.search}`;
      } catch (e) {
        return template.replace(/product_id=[^&]+/, `product_id=${productId}`);
      }
    }

    repositionAfterAnchor(anchorKey) {
      const row = this.closest('tr.cart-pair-it-with-row');
      const tbody = row?.closest('tbody');
      if (!row || !tbody || !anchorKey) return;

      const anchorRow = tbody.querySelector(`tr.cart-item[data-line-key="${CSS.escape(anchorKey)}"]`);
      if (!anchorRow || anchorRow.nextElementSibling === row) return;

      anchorRow.insertAdjacentElement('afterend', row);
    }

    bindEvents() {
      if (this._eventsBound) return;
      this._eventsBound = true;

      this.addEventListener('click', (event) => {
        const dismiss = event.target.closest('[data-pair-it-with-dismiss]');
        if (dismiss) {
          event.preventDefault();
          this.dismiss();
          return;
        }

        const addButton = event.target.closest('[data-pair-it-with-add]');
        if (addButton) {
          event.preventDefault();
          this.handleAdd(addButton);
        }
      });
    }

    dismiss() {
      const productId = this.dataset.productId;
      const cartDrawer = this.getCartDrawer();
      if (cartDrawer && productId && typeof cartDrawer.dismissPairItWith === 'function') {
        cartDrawer.dismissPairItWith(productId);
      }
      this.removeModule();
    }

    removeModule() {
      this._abortController?.abort();
      this.hidden = true;
      this.innerHTML = '';
      this.closest('tr.cart-pair-it-with-row')?.remove();
    }

    handleAdd(button) {
      if (this._adding || button.disabled || button.classList.contains('loading')) return;

      const variantId = button.dataset.variantId;
      if (!variantId) return;

      const cartDrawer = this.getCartDrawer();
      if (!cartDrawer || typeof fetchConfig !== 'function' || !window.routes?.cart_add_url) return;

      this._adding = true;
      button.setAttribute('aria-disabled', 'true');
      button.classList.add('loading');
      button.querySelector('.loading-overlay__spinner')?.classList.remove('hidden');

      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];

      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');
      const anchorKey = this.dataset.anchorKey;
      if (anchorKey) {
        formData.append('properties[_pair_it_with]', 'true');
        formData.append('properties[_pair_it_with_anchor]', anchorKey);
      }
      formData.append(
        'sections',
        cartDrawer.getSectionsToRender().map((section) => section.section || section.id),
      );
      formData.append('sections_url', window.location.pathname);
      config.body = formData;

      fetch(window.routes.cart_add_url, config)
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            throw new Error(response.description || response.message || 'Add to cart failed');
          }
          if (typeof cartDrawer.renderContents === 'function') {
            cartDrawer.renderContents(response);
          }
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'cart-pair-it-with',
              productVariantId: variantId,
              drawerSectionsRefreshed: true,
            });
          }
          this.removeModule();
        })
        .catch(() => {})
        .finally(() => {
          this._adding = false;
          button.classList.remove('loading');
          button.removeAttribute('aria-disabled');
          button.querySelector('.loading-overlay__spinner')?.classList.add('hidden');
        });
    }

    fetchRecommendationHtml(url, signal) {
      return fetch(url, {
        signal,
        credentials: 'same-origin',
        headers: { Accept: 'text/html' },
      })
        .then((response) => {
          if (!response.ok) throw new Error('Pair it with recommendations failed');
          return response.text();
        })
        .then((text) => parsePairItWithRecommendationHtml(text));
    }

    loadRecommendations() {
      const anchor = this.getActiveAnchor();
      if (!anchor || !this.isConnected) {
        this.removeModule();
        return;
      }

      if (this.isDismissed()) {
        this.removeModule();
        return;
      }

      if (this.querySelector('.cart-pair-it-with__inner')) {
        this.hidden = false;
        this.repositionAfterAnchor(anchor.anchorKey);
        this.closest('tr.cart-pair-it-with-row')?.removeAttribute('aria-hidden');
        return;
      }

      this._abortController?.abort();
      this._abortController = new AbortController();
      const { signal } = this._abortController;

      this.fetchRecommendationHtml(anchor.url, signal)
        .then((html) => {
          if (signal.aborted || !this.isConnected) return;
          if (!html) {
            this.removeModule();
            return;
          }

          this.innerHTML = html;
          this.hidden = false;
          this.repositionAfterAnchor(anchor.anchorKey);
          this.closest('tr.cart-pair-it-with-row')?.removeAttribute('aria-hidden');
        })
        .catch((error) => {
          if (error?.name === 'AbortError') return;
          this.removeModule();
        });
    }
  }

  customElements.define('cart-pair-it-with', CartPairItWith);
}
